import { gql } from "generated-graphql";
import {
  ChemicalPickerFragment,
  ChemicalsQueryQuery,
} from "generated-graphql/graphql";
import { forwardRef } from "react";
import { ControllerFieldState } from "react-hook-form";
import { formatChemicalName } from "util/chemicalName";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import { FormTextField } from "./Forms/FormTextField";

gql(`
  fragment ChemicalPicker on Chemical {
    id
    name
    pureOrMixture
    stateOfMatter
    casNumber
    isEhs
    alternateId
    stateFields {
      key
      value
      jurisdiction
    }
    components {
      name
      casNumber
      componentPercentage
      isEhs
    }
  }
`);

type Chemical = ChemicalsQueryQuery["chemicals"]["items"][number];

const CHEMICALS_QUERY = gql(`
  query ChemicalsQuery($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!], $facilityId: ID, $reportingYear: Int) {
    chemicals(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...ChemicalPicker
        facilityChemicals(facilityId: $facilityId, reportingYear: $reportingYear) {
          id
        }
      }
      count
    }
  }
`);

interface ChemicalPickerProps extends ControllerFieldState {
  onChange: (chemical: Chemical | null) => void;
  defaultSearchTerm?: string;
  disabledChemicalsById?: string[];
  disabledOptionLabel?: string;
  value?: ChemicalPickerFragment | null;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  isOptionDisabled?: (chemical: Chemical) => boolean;
  facilityId?: string;
  reportingYear?: number;
}

export const ChemicalPicker = forwardRef<HTMLDivElement, ChemicalPickerProps>(
  function ChemicalPicker(
    {
      onChange,
      defaultSearchTerm = "",
      disabledChemicalsById = [],
      disabledOptionLabel = "",
      value: initialValue,
      error,
      disabled,
      readOnly,
      isOptionDisabled,
      facilityId,
      reportingYear,
      required,
    },
    ref
  ) {
    const componentLabel = "Chemical Name";

    return (
      <>
        {readOnly ? (
          <FormTextField
            label={componentLabel}
            readOnly
            defaultValue={initialValue ? formatChemicalName(initialValue) : ""}
          />
        ) : (
          <AsyncAutocomplete
            ref={ref}
            query={CHEMICALS_QUERY}
            variables={{ facilityId, reportingYear }}
            initialPageSize={25}
            getItems={(data: ChemicalsQueryQuery) => data.chemicals.items}
            getOptionLabel={(option: Chemical) => formatChemicalName(option)}
            placeholderLabel={componentLabel}
            additionalSearchString={defaultSearchTerm}
            onChange={(val: Chemical | null) => onChange(val)}
            value={initialValue}
            error={error}
            disabled={disabled}
            lazyLoad
            textFieldProps={{ required }}
            getOptionDisabled={(option: Chemical) =>
              isOptionDisabled?.(option) ||
              disabledChemicalsById.includes(option?.id ?? "")
            }
            renderOption={(props, option: Chemical) => (
              <li {...props} key={option.id}>
                {formatChemicalName(option)}
                {disabledChemicalsById.includes(option?.id ?? "") && (
                  <span>&nbsp;({disabledOptionLabel})</span>
                )}
              </li>
            )}
            noOptionsText="Search for a chemical in your catalog"
          />
        )}
      </>
    );
  }
);
